// Where an eBay reply goes (2026-10-27, Jenn's spec "Agent drafts on eBay
// cases, inquiries and returns"; docs/contact-view.md, "The reply's
// destination"). On the agent draft layout (A2), the reply box on an eBay
// contact whose order has an open eBay inquiry or return IS the message to
// the buyer in that case: CXHub posts it to the case
// (server/src/services/ebay-reply-destination.js), and there is no separate
// "respond in the case" step with its own message box.
//
//   inquiry: post-order sendInquiryMessage(inquiryId, text)
//   return:  post-order sendReturnMessage(returnId, text)
//   case:    an escalated eBay case (casemanagement). eBay's Post-Order API
//            has no message call for a case, so the reply goes as a member
//            message on the order thread, and the destination line says so.
//   member:  no open case: a regular eBay member message, as before.
//
// Pure: the server builds the destination at generation
// (modelMeta.replyDestination) and the send path and both panels read it.

// eBay Post-Order InquiryStatusEnum's closed values (ebay-case-lookup.js's
// CLOSED_INQUIRY_STATUSES; the same three).
const CLOSED_INQUIRY = new Set(['CLOSED', 'CLOSED_WITH_ESCALATION', 'CS_CLOSED'])

export function isClosedInquiryStatus(status) {
  return CLOSED_INQUIRY.has(String(status ?? '').trim().toUpperCase())
}

// A return is open unless eBay's status says closed (CLOSED, RETURN_CLOSED,
// CS_CLOSED and the like). A missing status is open (the search found it).
export function isClosedReturnStatus(status) {
  return /CLOSED/i.test(String(status ?? ''))
}

// A case (casemanagement) is open unless its status says closed.
export function isClosedCaseStatus(status) {
  return /CLOSED/i.test(String(status ?? ''))
}

// The destination for a contact: { kind, id, status, noReply, reason }.
//   channel: the conversation's channel (only 'ebay' has a case
//     destination; every other channel is null: the reply goes as today).
//   caseContext: shadow-inputs.js's buildCaseContext().context ({ kind
//     'INR inquiry' | 'eBay inquiry' | 'eBay case', id, status,
//     waitingForBuyer, buyerSpokeSinceOurLast }) or null.
//   ebayReturn: the order's eBay return (enrichment.summary.ebayReturnFound,
//     { id, status }) or null.
// An open inquiry wins, then an open return, then an open escalated case.
// noReply: the case is waiting for the buyer and the buyer has not written
// since our last message (the rule for waiting on the buyer stays).
export function replyDestinationFor({ channel = null, caseContext = null, ebayReturn = null } = {}) {
  if (channel !== 'ebay') return null
  const c = caseContext?.id ? caseContext : null
  const waiting = Boolean(c?.waitingForBuyer) && c?.buyerSpokeSinceOurLast !== true
  if (c && /inquiry/i.test(String(c.kind ?? '')) && !isClosedInquiryStatus(c.status)) {
    return { kind: 'inquiry', id: String(c.id), status: c.status ?? null, noReply: waiting }
  }
  if (ebayReturn?.id && !isClosedReturnStatus(ebayReturn.status)) {
    return { kind: 'return', id: String(ebayReturn.id), status: ebayReturn.status ?? null, noReply: /WAITING_BUYER|WAITING_FOR_BUYER/i.test(String(ebayReturn.status ?? '')) }
  }
  if (c && /case/i.test(String(c.kind ?? '')) && !isClosedCaseStatus(c.status)) {
    return { kind: 'case', id: String(c.id), status: c.status ?? null, noReply: waiting }
  }
  return { kind: 'member', id: null, status: null, noReply: false }
}

// Whether the reply is posted to the case itself (an inquiry or a return).
export function postsToCase(dest) {
  return Boolean(dest?.id) && (dest.kind === 'inquiry' || dest.kind === 'return')
}

const CASE_NAMES = { inquiry: 'eBay inquiry', return: 'eBay return', case: 'eBay case' }
export function caseName(dest) {
  return dest?.id && CASE_NAMES[dest.kind] ? `${CASE_NAMES[dest.kind]} ${dest.id}` : null
}

// The line under the reply box's To line (admin- and CSR-visible):
//   "Sends to eBay inquiry 5123456789"
//   "Sends to eBay return 5098765432"
//   "Sends as an eBay member message: eBay has no message call for eBay case 5011111111"
//   "Sends to eBay member message"
//   "No reply: the inquiry is waiting for the buyer" (hasReply false and
//     the case waits for the buyer)
// hasReply: whether there is a reply to send (the waiting line shows only
// without one; a person who writes a reply anyway sends it to the case).
export function replyDestinationLine(dest, { hasReply = true } = {}) {
  if (!dest) return null
  if (!hasReply && dest.noReply && dest.kind !== 'member') {
    return `No reply: the ${dest.kind === 'case' ? 'case' : dest.kind} is waiting for the buyer`
  }
  if (postsToCase(dest)) return `Sends to ${caseName(dest)}`
  if (dest.kind === 'case' && dest.id) return `Sends as an eBay member message: eBay has no message call for ${caseName(dest)}`
  return 'Sends to eBay member message'
}

// The Activity Log's words for the send (send-approved-reply.js).
export function replyPostedNote(dest) {
  return postsToCase(dest) ? `Reply posted to ${caseName(dest)}` : 'Reply sent as an eBay member message'
}
export function replyFallbackNote(dest, why = 'closed') {
  return `${caseName(dest) ?? 'The eBay case'} was ${why} at Send: the reply was sent as an eBay member message instead`
}
export function caseNoMessageCallNote(dest) {
  return `eBay has no message call for ${caseName(dest) ?? 'this eBay case'}: the reply was sent as an eBay member message`
}

// The steps that only message the buyer in the case: on the A2 layout with
// the reply posted to the case they are left out (the reply is the case
// message, sent once). The manual inquiry reply (inr_request_id_process with
// no disposition), the inquiry message (inr_send_message) and a task's case
// message.
export function isCaseMessageStep(a) {
  if (!a?.type) return false
  if (a.type === 'inr_send_message' || a.type === 'task_case_message') return true
  return a.type === 'inr_request_id_process' && !a.params?.disposition
}

// The case actions that carry their own buyer-facing comment field in
// eBay's API (action-executor.js reads rawInput.editedMessage for both): the
// inquiry refund's comments and the shipment proof's sellerComments. On the
// A2 layout with the reply posted to that inquiry, the comment is the reply,
// and the reply is not sent again.
export const CASE_COMMENT_STEP_TYPES = Object.freeze(['inr_issue_refund', 'inr_provide_shipment_info'])

// The index of the step that carries the reply as its comment, or -1: the
// first comment step on the destination's inquiry that is checked (checked:
// a Set or an array of indexes; null means every step) and not done yet
// (done: indexes already processed).
export function replyCarrierStepIndex(actions = [], dest = null, { checked = null, done = [] } = {}) {
  if (dest?.kind !== 'inquiry' || !dest.id) return -1
  const isOn = (i) => (checked == null ? true : (checked instanceof Set ? checked.has(i) : checked.includes(i)))
  return (actions ?? []).findIndex((a, i) => CASE_COMMENT_STEP_TYPES.includes(a?.type)
    && String(a?.params?.ebayCase ?? '') === String(dest.id) && isOn(i) && !(done ?? []).includes(i))
}
